import type { Translations } from "./ko";

export const en: Translations = {
  app: {
    name: "ARGOS",
    title: "Command & Control",
    subtitle: "4 Drones + 2 Humanoids Swarm Autonomous Mission",
    footer: "ACC v0.1.0 · Apache-2.0 · Daegu Gangbuk Fire Station, Minbal",
  },
  nav: {
    command: "Command",
    dashboard: "Dashboard",
    cop: "COP",
    missions: "Missions",
    robots: "Robots",
    alerts: "Alerts",
    audit: "Audit",
    twofa: "2FA",
    logout: "Logout",
  },
  role: {
    commander: "Commander",
    vice: "Deputy",
    controller: "Operator",
    sim: "Simulator",
    admin: "Admin",
  },
  login: {
    username: "Username",
    password: "Password",
    submit: "Sign In",
    submitting: "Signing in...",
    failed: "Sign-in failed",
    twofa_title: "Two-factor authentication",
    twofa_hint: "Enter 6-digit authenticator code or 8-char backup code",
    twofa_placeholder: "123456 or ABCD1234",
    twofa_submit: "Verify",
    twofa_submitting: "Verifying...",
    twofa_back: "← Back to sign-in",
    twofa_failed: "OTP verification failed",
  },
  common: {
    loading: "Loading…",
    error: "Error",
    retry: "Retry",
    cancel: "Cancel",
    save: "Save",
    delete: "Delete",
    confirm: "Confirm",
    none: "—",
  },
  status: {
    pending: "Pending",
    running: "Running",
    paused: "Paused",
    completed: "Completed",
    aborted: "Aborted",
    error: "Error",
  },
  severity: {
    critical: "Critical",
    high: "High",
    medium: "Medium",
    low: "Low",
  },
  dashboard: {
    drones_count: "Drones (Swarm)",
    humanoids: "Humanoids",
    active_alerts: "Active Alerts",
    no_drones: "No drones registered. Contact admin.",
    recent_alerts: "Recent Alerts (live WebSocket)",
    no_alerts: "No alerts",
    operations_3d: "3D Operations (live WebSocket)",
    controls_help: "Drag: orbit · wheel: zoom · right-drag: pan",
    fire_legend: "Fire Category Legend (K-class)",
    fire_legend_general: "A · General (wood, paper) · Water/foam OK",
    fire_legend_oil: "B · Oil (gasoline, fuel) · Foam/CO2 (no water)",
    fire_legend_electrical: "C · Electrical (panels, wires) · CO2/powder (no water)",
    fire_legend_metal: "D · Metal (Mg, Ti) · Special D-class powder",
    fire_legend_gas: "E · Gas (LPG, city gas) · Shut-off first + cool",
    fire_count_active: "Active Fires (by category)",
    drone_capable: "Drone capable",
    humanoid_capable: "Humanoid capable",
    sim2real_title: "Sim2Real Gap (predicted vs actual position)",
    sim2real_no_data: "No data — run simulation + real telemetry required",
    sim2real_n_pairs: "Matched pairs",
    sim2real_mean: "Mean gap (m)",
    sim2real_p50: "Median (m)",
    sim2real_p95: "p95 (m)",
    sim2real_max: "Max (m)",
    sim2real_by_robot: "Per-robot gap",
    sim2real_warning: "Above 1m threshold — sim accuracy low",
    sim2real_ok: "Within 1m threshold — sim trusted",
    sim2real_all_missions: "All missions (aggregate)",
    sim2real_select_mission: "Select mission",
    chaos_title: "Chaos Simulation Stats",
    mission_state_title: "Mission State (Orchestrator)",
    mission_state_unavailable: "ROS disconnected or mission not started — waiting for rosbridge",
    mission_state_robots: "Active robots",
    mission_state_fires: "Active fires",
    mission_state_victims: "Victims detected",
    mission_state_critical_unassigned: "{{count}} critical task(s) unassigned",
    mission_state_critical_hint: "No capable robot — consider reinforcement or manual dispatch",
    mission_state_age: "{{age}}s ago",
    mission_state_stale: "⚠ {{age}}s old — possibly stale",
    mission_state_not_converged: "CBBA allocation not converged — assignment may be suboptimal",
    victim_board_title: "Victim Board (#142)",
    victim_board_unavailable: "ROS disconnected or no data — waiting for rosbridge",
    victim_board_count: "{{identified}} identified + {{unidentified}} unidentified",
    victim_board_empty: "No victims detected",
    victim_board_unidentified: "unidentified",
    victim_board_priority: "P{{p}}",
    victim_board_age: "{{age}}s ago",
    victim_board_stale: "⚠ {{age}}s old — possibly stale",
    chaos_no_events: "No chaos events",
    chaos_disables: "Disables",
    chaos_revives: "Auto-revives",
    chaos_currently_down: "Currently down",
    chaos_mean_downtime: "Mean downtime",
    chaos_max_downtime: "Max downtime",
    chaos_by_robot: "By robot",
    chaos_currently_disabled: "DOWN",
    dispatch_stats_title: "Operator Engage Dispatch Stats",
    dispatch_total: "Total dispatches",
    dispatch_last_24h: "Last 24h",
    dispatch_by_user: "By operator",
    dispatch_by_category: "By category",
    dispatch_no_events: "No dispatch records",
    mission_stats_title: "Mission Cumulative Stats",
    mission_total: "Total missions",
    mission_completion: "Completion rate",
    mission_avg_duration: "Avg duration",
    mission_last_24h: "24h started",
    mission_no_data: "No mission records",
    dispatch_history_title: "Recent Engage Dispatches",
    dispatch_history_empty: "No dispatch history",
    dispatch_history_count: "Last {{n}} entries",
    health_title: "Operational Readiness",
    health_status_ok: "OK",
    health_status_warning: "Warning",
    health_status_critical: "Critical",
    health_chaos_active: "Chaos down",
    health_sim2real_gap: "Sim2Real gap",
    health_running_missions: "Running missions",
    health_offline_robots: "Offline robots",
  },
  missions: {
    title: "Missions",
    title_count: "Missions ({{count}})",
    new: "New Mission",
    none: "No missions. Click \"New Mission\" at top-right.",
    col_name: "Name",
    col_status: "Status",
    col_env: "Environment",
    col_started: "Started",
    col_control: "Control",
    btn_start: "Start",
    btn_pause: "Pause",
    btn_resume: "Resume",
    btn_abort: "Abort",
    btn_replay: "Replay",
    create_title: "Create New Mission",
    name_label: "Mission Name",
    name_placeholder: "e.g. 2F Office Fire Suppression #1",
    mode_label: "Creation Mode",
    mode_sim: "Random Scenario + Run Sim",
    mode_manual: "Manual",
    n_drones: "Drone Count",
    n_humanoids: "Humanoid Count",
    create_failed: "Mission creation failed",
    create_btn: "Create",
    creating: "Creating…",
    abort_title: "Force Abort Mission",
    abort_desc: "Aborting \"{{name}}\" will recall all robots immediately. Active autonomous policy will be cancelled.",
    benchmark_label: "Run A/B comparison (cbba vs random)",
    benchmark_help: "Episode count (3~20, 0=disabled)",
    benchmark_result: "A/B Comparison Result",
    benchmark_winner: "Winner",
    benchmark_cbba: "cbba avg reward",
    benchmark_random: "random avg reward",
    benchmark_delta: "Success rate delta",
  },
  replay: {
    loading: "Loading replay…",
    load_failed: "Failed to load replay",
    title: "Replay",
    back_to_missions: "← Missions",
    duration_summary: "{{seconds}}s · {{points}} telemetry · {{alerts}} alerts · {{percent}}% complete",
    download_aar: "Download AAR",
    tab_replay: "Replay",
    tab_aar: "AAR Report",
    engage_events_title: "Engage Events (chronological)",
    stat_telemetry: "Total telemetry",
    stat_alerts: "Alerts",
    stat_assignments: "Completed",
    stat_completion: "Completion rate",
    stat_engage: "Engage events",
  },
  dispatch: {
    title: "Engage Dispatch — Fire #{{id}}",
    category: "Category",
    location: "Location",
    severity: "Severity",
    recommended: "Recommended robot",
    candidates: "Candidates ({{n}})",
    no_capable: "No capable robot",
    confirm: "Dispatch engage command",
    cancel: "Cancel",
    dispatching: "Dispatching…",
    dispatched: "Dispatched",
    failed: "Dispatch failed",
    distance: "{{m}}m away",
    no_position: "No position",
  },
  alerts_page: {
    realtime: "● Live",
    no_alerts: "No alerts",
    resolved: "Resolved",
    acknowledged: "Acknowledged",
    unack: "Unacknowledged",
  },
  twofa: {
    title: "Two-Factor Authentication (TOTP)",
    enabled: "Enabled",
    disabled: "Disabled",
    backup_remaining: "Backup codes remaining",
    setup_recommend: "Strongly recommended for admin·commander roles",
    start_setup: "Start 2FA Setup",
    qr_title: "Scan QR Code",
    qr_hint: "Use Google Authenticator, 1Password, Authy etc. to scan the QR and enter the 6-digit code.",
    secret_label: "Manual entry secret",
    code_placeholder: "6-digit code",
    enable: "Verify & Enable",
    backup_title: "Backup Codes (store safely — shown once)",
    backup_hint: "If you lose your authenticator, each backup code grants one login. They are consumed on use.",
    backup_done: "I've stored them — Close",
    manage_title: "Manage",
    manage_placeholder: "Current 6-digit or backup code",
    btn_regen: "Regenerate Backup Codes",
    btn_disable: "Disable 2FA",
    setup_failed: "Setup failed",
    verify_failed: "Verify failed",
    disable_failed: "Disable failed",
    regen_failed: "Regeneration failed",
  },
};
